import * as d3 from "d3";
import * as topojson from "topojson";
import { group } from "d3-array";

const preprocess = (d) => {
  d.forEach((v) =>
    v[1].forEach((x) => {
      x.date = new Date(x.date);
      x.grocery_and_pharmacy_percent_change_from_baseline = parseInt(
        x.grocery_and_pharmacy_percent_change_from_baseline
      );
      x.parks_percent_change_from_baseline = parseInt(
        x.parks_percent_change_from_baseline
      );
      x.residential_percent_change_from_baseline = parseInt(
        x.residential_percent_change_from_baseline
      );
      x.retail_and_recreation_percent_change_from_baseline = parseInt(
        x.retail_and_recreation_percent_change_from_baseline
      );
      x.transit_stations_percent_change_from_baseline = parseInt(
        x.transit_stations_percent_change_from_baseline
      );
      x.workplaces_percent_change_from_baseline = parseInt(
        x.workplaces_percent_change_from_baseline
      );
    })
  );
  return d;
};

window.onload = function () {
  d3.json("./data/google_mobility_short.json").then((d) => {
    d = preprocess(d);
    const figure = new TimeFigure(
      document.querySelector("figure.small-multiples"),
      d
    );
    const park = new ParkFigure(document.querySelector("figure.parks"), d);
    const reopen = new ReopeningFigure(
      document.querySelector("figure.reopening"),
      d
    );
  });
};

const movingAverage = (values, N) => {
  // https://observablehq.com/@d3/moving-average
  // modified to handle missing values
  // someone check this to make sure it's right -- it looks ok visually
  let i = 0;
  let sum = 0;
  const means = new Float64Array(values.length).fill(NaN);
  for (let n = Math.min(N - 1, values.length); i < n; ++i) {
    sum += values[i];
  }
  for (let n = values.length; i < n; ++i) {
    if (!isNaN(values[i])) {
      sum += values[i];
      means[i] = sum / N;
    } else {
      means[i] = NaN;
    }
    if (!isNaN(values[i - N + 1])) sum -= values[i - N + 1];
  }
  return means;
};

const rate = (values) => {
  return values.slice(1).map((d, i) => d - values[i]);
};

const zip = (a, b) => {
  return a.map((d, i) => [d, b[i]]);
};

class Figure {
  title = "Figure Title";
  byline = "Data Team / Michigan Daily";
  caption = "Figure caption goes here";

  constructor(el, data) {
    this.el = d3.select(el).datum(data);
  }

  setup() {
    this.el
      .selectAll("h3.figure__title")
      .data([this.title])
      .enter()
      .append("h3")
      .attr("class", "figure__title")
      .text((d) => d);
    this.el
      .selectAll("div.figure__caption")
      .data([this.caption])
      .enter()
      .append("div")
      .attr("class", "figure__caption")
      .text((d) => d);
    this.graphic = this.el.append("div").attr("class", "figure__graphic");
    this.el.append("span").attr("class", "figure__byline").text(this.byline);
  }
  processData() {}
  draw() {}
}

class ParkFigure extends Figure {
  title = "Parks, a Popular Pastime";
  byline = "Naitian Zhou / Michigan Daily";
  caption = `Here are some counties with the largest uptick in park activity,
    as a moving average over 7 days.`;
  constructor(el, data) {
    super(el, data);
    this.data = this.processData();
    this.chart = new MultiTimeChart();
    this.chart.width = 0.8 * window.innerWidth;
    this.chart.margin.right = 2;
    this.chart.xAxis.tickFormat(d3.timeFormat("%m-%d"));
    this.chart.line.defined((d) => !isNaN(d[1]));
    this.chart.xScale.nice();
    this.chart.yScale.nice();
    this.setup();
  }

  processData() {
    return this.el.datum().map((d) => {
      return [
        d[0],
        zip(
          d[1].map((d) => d.date),
          movingAverage(
            d[1].map((d) => d.parks_percent_change_from_baseline),
            7
          )
        ).slice(7),
      ];
    });
  }

  setup() {
    super.setup();
    this.draw();
  }

  draw() {
    super.draw();
    this.el.select(".figure__graphic").datum(this.data).call(this.chart.draw);
  }
}

class ReopeningFigure extends Figure {
  title = "What Places are Seeing the Biggest Uptick in Activity?";
  byline = "Naitian Zhou / Michigan Daily";
  caption = `Counties colored more green are seeing an increase in activity over
  the past week. Gray counties don't have sufficient data. Activity change is
  calculated as the cumulative rolling average change in mobility in retail and
  recreation areas over a week.`;
  constructor(el, data) {
    super(el, data);
    this.mapchart = new MapChart();
    this.data = this.processData();
    this.mapchart.loadMap().then(this.setup.bind(this));
  }

  setup() {
    super.setup();
    this.graphic.append("div").attr("class", "map");
    this.data = this.processData();
    this.draw();
  }

  processData() {
    let d = this.el.datum();
    // we define a recent increase as increase over the past week of baseline
    // percentage.
    return d.map((v) => {
      return {
        county: v[0],
        fillValue: d3.sum(
          rate(
            movingAverage(
              v[1].map(
                (x) => x.retail_and_recreation_percent_change_from_baseline
              ),
              7
            )
          ).slice(-7)
        ),
      };
    });
  }

  draw() {
    this.graphic.selectAll("div.map").datum(this.data).call(this.mapchart.draw);
  }
}

class TimeFigure extends Figure {
  title = "Mobility in Michigan by County";
  byline = "Naitian Zhou / Michigan Daily";
  caption = `Explore the raw data for all counties. Blank sections are missing
  data, where a location did not have enough traffic to preserve privacy when
  recording the data. The red line is a 7-day moving average.`;
  chart = new TimeChart();

  constructor(el, data) {
    super(el, data);
    this.setup();
  }

  setup() {
    super.setup();
    this.metric = "grocery_and_pharmacy_percent_change_from_baseline";
    const dropdown = this.graphic.append("select");
    dropdown
      .selectAll("option")
      .data([
        {
          display: "Grocery and Pharmacy",
          value: "grocery_and_pharmacy_percent_change_from_baseline",
        },
        { display: "Parks", value: "parks_percent_change_from_baseline" },
        {
          display: "Residential",
          value: "residential_percent_change_from_baseline",
        },
        {
          display: "Retail and Recreation",
          value: "retail_and_recreation_percent_change_from_baseline",
        },
        {
          display: "Transit Stations",
          value: "transit_stations_percent_change_from_baseline",
        },
        {
          display: "Workplaces",
          value: "workplaces_percent_change_from_baseline",
        },
      ])
      .enter()
      .append("option")
      .text((d) => d.display)
      .attr("value", (d) => d.value);
    dropdown.on(
      "change",
      function () {
        this.metric = d3.event.target.value;
        this.draw();
      }.bind(this)
    );
    const small_mults = this.graphic
      .append("div")
      .attr("class", "figure__container");
    this.data = this.processData();
    this.draw();
  }

  processData() {
    return this.el.datum();
  }

  draw() {
    super.draw();
    const data = this.data.filter(
      (d) =>
        d[1]
          .slice(1)
          .reduce(
            (acc, v, i) =>
              (acc +=
                !isNaN(v[this.metric]) && !isNaN(d[1][i][this.metric]) ? 1 : 0),
            0
          ) > 1
    );
    const figure = this.graphic.select("div.figure__container");
    this.chart.x((d) => d.date).y((d) => d[this.metric]);
    this.chart.height = 150;
    this.chart.width = 150;
    this.chart.margin.right = 10;
    this.chart.xAxis.tickFormat(d3.timeFormat("%m-%d"));
    this.chart.xAxis.ticks(2);
    this.chart.yAxis.ticks(5);
    this.chart.line.defined((d) => !isNaN(d[this.metric]));
    this.chart.xScale.nice();
    this.chart.yScale.nice();

    const divs = figure.selectAll("div").data(data, (d) => d[0]);

    divs
      .enter()
      .append("div")
      .append("h3")
      .text((d) => d[0]);

    divs.exit().remove();
    figure
      .selectAll("div")
      .datum((d) => d[1])
      .call(this.chart.draw);
    // d3.select(figure).datum(data).call(chart);
  }
}

class Chart {
  width = 600;
  height = 300;
  margin = { top: 5, right: 0, bottom: 30, left: 30 };

  xScale = d3.scaleLinear();
  yScale = d3.scaleLinear();

  constructor() {
    this.setupChart = this.setupChart.bind(this);
    this.chart = this.chart.bind(this);
    this.draw = this.draw.bind(this);
  }

  setupChart(el, datum, i) {
    // this selects the SVG if it exists
    const svg = el.selectAll("svg").data([datum]);
    // if SVG doesn't exist, create it and append the g
    const g = svg
      .enter()
      .append("svg")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("class", `chart ${i}`)
      .append("g");
    g.attr("width", this.width - this.margin.left - this.margin.right)
      .attr("height", this.height - this.margin.top - this.margin.bottom)
      .attr("transform", `translate(${this.margin.left}, ${this.margin.top})`);
    return g;
  }

  chart(el, datum, i) {
    this.setupChart(el, datum, i);
    return el.selectAll("svg").select("g");
  }

  draw(selection) {
    // this.width = window.innerWidth * 0.8;
    // this is hacky because of `this` sorry :(
    const chart = this.chart;
    selection.each(function (d, i) {
      // draw chart
      chart(d3.select(this), d, i);
    });
  }
}

const axisMixin = (Base) =>
  class extends Base {
    xAxis = d3.axisBottom().scale(this.xScale);
    yAxis = d3.axisLeft().scale(this.yScale);

    setupChart(el, datum, i) {
      // axes
      const g = super.setupChart(el, datum, i);
      g.append("g")
        .attr("class", "graph__xaxis")
        .attr(
          "transform",
          `translate(0, ${this.height - this.margin.top - this.margin.bottom})`
        );
      g.append("g").attr("class", "graph__yaxis");
      return g;
    }

    chart(el, datum, i) {
      const g = super.chart(el, datum, i);
      this.xAxis.scale(this.xScale);
      this.yAxis.scale(this.yScale);
      g.select("g.graph__xaxis").call(this.xAxis);
      g.select("g.graph__yaxis").call(this.yAxis);
      return g;
    }
  };

class TimeChart extends axisMixin(Chart) {
  xValue = (d) => d[0];
  yValue = (d) => d[1];

  line = d3.line();
  avgline = d3.line().defined((d) => !isNaN(d[1]));

  constructor() {
    super();
    this.x = this.x.bind(this);
    this.y = this.y.bind(this);
  }

  setupChart(el, datum, i) {
    // this selects the SVG if it exists
    const g = super.setupChart(el, datum, i);
    // set up chart elements
    // we're safe to use append here because g is the "enter" selection
    // line
    g.append("path").attr("class", "graph__line");
    g.append("path").attr("class", "graph__avg");
    return g;
  }

  chart(el, datum, i) {
    this.xScale
      .domain(d3.extent(datum.map(this.xValue)))
      .range([0, this.width - this.margin.left - this.margin.right]);
    this.yScale
      .domain(d3.extent(datum.map(this.yValue)))
      .range([this.height - this.margin.top - this.margin.bottom, 0]);

    this.line
      .x((d) => this.xScale(this.xValue(d)))
      .y((d) => this.yScale(this.yValue(d)));

    // FIXME: calling super in the middle kind of sucks... right now, it's
    // necessary for setting axes because otherwise scales aren't set. how to
    // fix?
    const g = super.chart(el, datum, i);
    g.select("path.graph__line")
      .datum(datum)
      .attr("d", this.line)
      .attr("fill", "none")
      .attr("stroke", "black");
    const mvAvg = movingAverage(datum.map(this.yValue), 7).map(this.yScale);
    g.select("path.graph__avg")
      .datum(zip(datum.map(this.xValue).map(this.xScale), mvAvg))
      .attr("d", this.avgline)
      .attr("fill", "none")
      .attr("stroke", "red");
    return g;
  }

  x(fn) {
    this.xValue = fn;
    return this;
  }

  y(fn) {
    this.yValue = fn;
    return this;
  }
}

class MultiTimeChart extends axisMixin(Chart) {
  xValue = (d) => d[0];
  yValue = (d) => d[1];
  line = d3.line();
  // avgline = d3.line().defined((d) => !isNaN(d[1]));

  constructor() {
    super();
    this.x = this.x.bind(this);
    this.y = this.y.bind(this);
  }

  setupChart(el, datum, i) {
    const g = super.setupChart(el, datum, i);
    g.selectAll("path.graph__line")
      .data(datum)
      .enter()
      .append("path")
      .attr("class", "graph__line")
      .attr("fill", "none")
      .attr("stroke", "black");
    g.selectAll("text")
      .data(datum.map((d) => d[0]))
      .enter()
      .append("text")
      .text((d) => d)
      .attr("class", (d) => `graph__label ${d}`)
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "hanging");
    this.yAxis.tickSize(-this.width + this.margin.left);
    return g;
  }

  getMin(datum, acc) {
    return d3.min(datum.map((d) => d3.min(d[1], acc)));
  }

  getMax(datum, acc) {
    return d3.max(datum.map((d) => d3.max(d[1], acc)));
  }

  chart(el, datum, i) {
    // get global max and min
    const xExtent = [
      this.getMin(datum, this.xValue),
      this.getMax(datum, this.xValue),
    ];
    this.xScale
      .domain(xExtent)
      .range([0, this.width - this.margin.left - this.margin.right]);
    const yExtent = [
      this.getMin(datum, this.yValue),
      this.getMax(datum, this.yValue),
    ];
    this.yScale
      .domain(yExtent)
      .range([this.height - this.margin.top - this.margin.bottom, 0]);
    this.line
      .x((d) => this.xScale(this.xValue(d)))
      .y((d) => this.yScale(this.yValue(d)))
      .curve(d3.curveMonotoneX);
    const g = super.chart(el, datum, i);
    g.selectAll("path.graph__line")
      .attr("d", (d) => {
        return this.line(d[1]);
      })
      .attr("class", (d) => `graph__line ${d[0]}`);
    g.selectAll("text.graph__label")
      .attr("x", this.xScale(xExtent[1]) - 5)
      .attr("y", (d) =>
        this.yScale(datum.find((x) => x[0] === d)[1].slice(-1)[0][1])
      )
      .attr("opacity", "0");
    return g;
  }

  x(fn) {
    this.xValue = fn;
    return this;
  }

  y(fn) {
    this.yValue = fn;
    return this;
  }
}

class MapChart extends Chart {
  height = 600;
  mapdataSrc =
    "https://raw.githubusercontent.com/deldersveld/topojson/master/countries/us-states/MI-26-michigan-counties.json";
  mapdataFeature = "cb_2015_michigan_county_20m";
  margin = { top: 0, right: 0, bottom: 0, left: 0 };
  mapKey = (d) => d.properties.NAME;
  dataKey = (d) => d.county;
  fillValue = (d) => d.fillValue;

  colorScale = d3.scaleSequential(d3.interpolatePRGn);

  constructor() {
    super();
  }

  loadMap() {
    return d3.json(this.mapdataSrc).then((topo) => {
      this.topo = topo;
      this.width = d3.min([window.innerWidth * 0.8, 500]);
      this.height = this.width * 1.2;
      this.path = d3
        .geoPath()
        .projection(
          d3
            .geoMercator()
            .fitSize(
              [
                this.width - this.margin.left - this.margin.right,
                this.height - this.margin.top - this.margin.bottom,
              ],
              topojson.feature(
                this.topo,
                this.topo.objects[this.mapdataFeature]
              )
            )
        );
      return new Promise((resolve) => {
        resolve(this);
      });
    });
  }

  setupChart(el, datum, i) {
    const g = super.setupChart(el, datum, i);
    if (!this.topo) return g;

    g.selectAll("path.map__interior")
      .data(
        topojson.feature(this.topo, this.topo.objects[this.mapdataFeature])
          .features
      )
      .enter()
      .append("path")
      .attr("class", "map__interior")
      .attr("fill", "#AAA")
      .attr("d", this.path)
      .append("title")
      .text((d) => d.properties.NAME);
    g.selectAll("path.map__border")
      .data([
        topojson.mesh(
          this.topo,
          this.topo.objects[this.mapdataFeature],
          (a, b) => a !== b
        ),
      ])
      .enter()
      .append("path")
      .attr("class", "map__border")
      .attr("stroke", "white")
      .attr("fill", "none")
      .attr("d", this.path);
    g.selectAll("path.map__outer")
      .data([
        topojson.mesh(
          this.topo,
          this.topo.objects[this.mapdataFeature],
          (a, b) => a === b
        ),
      ])
      .enter()
      .append("path")
      .attr("class", "map__outer")
      .attr("stroke", "black")
      .attr("fill", "none")
      .attr("d", this.path);
    return g;
  }

  chart(el, datum, i) {
    const g = super.chart(el, datum, i);
    if (!this.topo) return g;

    // this.interior.attr("fill", d => datum[mapKey(d)])
    let extent = d3.max(datum.map(this.fillValue).map(Math.abs));
    this.colorScale.domain([-extent, extent]);
    g.selectAll("path.map__interior").attr("fill", (d) => {
      if (!datum.find((v) => this.mapKey(d) === this.dataKey(v))) {
        return "#AAA";
      }
      if (
        !this.fillValue(datum.find((v) => this.mapKey(d) === this.dataKey(v)))
      ) {
        return "#AAA";
      }
      return this.colorScale(
        this.fillValue(datum.find((v) => this.mapKey(d) === this.dataKey(v)))
      );
    });
    return g;
  }
}
